import * as Form from "@radix-ui/react-form";
import { useCallback, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { emailExists, login } from "@/api";
import { Button, CheckboxInput, ColumnModal, Input } from "@/components";
import { useAppProperties } from "@/hooks";
import { Hide, Show } from "@/icons";
import { useAuthToken, useTranslate } from "@/providers/context";
import { cn, includesNumber, includesSpecialCharacter } from "@/utils";

import { UserScreenWrapper } from "./components/UserScreenWrapper.tsx";
import { useCreateCustomer } from "./hooks/useCreateCustomer.tsx";
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  secret: string;
  marketingOptIn: string; // "true" or "false"
}

const ALREADY_EXISTS = "This account already exists.";

export const SignUpScreen = () => {
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { translate } = useTranslate();
  const { setToken } = useAuthToken();
  const createCustomer = useCreateCustomer();
  const [searchParams] = useSearchParams();
  const { data } = useAppProperties();
  const redirect = searchParams.get("redirect");
  const emailInputRef = useRef<HTMLInputElement>(null); // Create a ref for the email input field

  // Hold the form values during the confirmation modal before calling API
  const [submissionValues, setSubmissionValues] = useState<
    FormData | undefined
  >(undefined);

  const onSubmit = useCallback(
    async (formData: FormData) => {
      setLoading(true);
      if (await emailExists({ email: formData.email })) {
        setError(ALREADY_EXISTS);
        setLoading(false);
        return;
      }
      setSubmissionValues(formData);
      setLoading(false);
    },
    [setSubmissionValues],
  );

  const signUp = useCallback(
    async ({
      email,
      firstName,
      lastName,
      secret,
      marketingOptIn,
    }: FormData) => {
      setLoading(true);

      setError("");

      createCustomer.mutate(
        {
          Name: `${firstName} ${lastName}`,
          Email: email,
          Secret: secret,
          MarketingOptIn: marketingOptIn === "true",
        },
        {
          onSuccess: async () => {
            try {
              console.info("Automatically logging in user");
              const token = await login(email, secret);
              if (token) {
                setToken(token);
                navigate("/for-sale");
                return;
              } else {
                setError("Failed to sign up");
              }
            } catch (e) {
              let message = "Failed to sign up";
              if (e instanceof Error) {
                message = e.message;
              }
              console.error(message);
              setError(message);
            } finally {
              setLoading(false); // End of successful path
            }
          },
          onError: () => {
            setLoading(false); // End of error path
          },
        },
      );
    },
    [createCustomer, setToken, navigate],
  );

  const togglePassword = useCallback(() => {
    setVisible((v) => !v);
  }, [setVisible]);

  const signInURL = redirect ? `/sign-in?redirect=${redirect}` : "/sign-in";

  const optInMarketing = data?.MarketingOptInOnSignUp === "0" ? false : true;

  return (
    <UserScreenWrapper
      showCloseButton
      contentClassName={cn(
        redirect ? "pb-[120px] sm:pb-0" : "pb-[85px] sm:pb-0",
      )}
    >
      <div className="text-item-primary">
        <h1 className="h1 mb-[12px]">{translate("Create your account")}</h1>
        {redirect ? (
          <h5 className="p1-regular sm:h5-regular mb-7 sm:mb-8">
            {translate("You do not have access to this content.")}{" "}
            {translate("Please sign up to continue.")}
          </h5>
        ) : (
          <h5 className="p1-regular sm:h5-regular mb-7 sm:mb-8">
            {translate("Already have an account?")}{" "}
            <span
              className="p1-semibold sm:h5-semibold cursor-pointer text-highlightPrimary"
              onClick={() => navigate("/sign-in", { replace: true })}
            >
              {translate("Sign in here")}
            </span>
          </h5>
        )}
      </div>
      <Form.Root
        onSubmit={(event) => {
          event.preventDefault();
          const data = Object.fromEntries(new FormData(event.currentTarget));
          onSubmit(data as unknown as FormData);
        }}
      >
        <Form.Field name="firstName">
          <div>
            <Form.Message className="text-error-item" match="valueMissing">
              {translate("Please enter your first name")}
            </Form.Message>
          </div>
          <Form.Control asChild required>
            <Input
              className="mb-[52px] sm:mb-11"
              type="text"
              placeholder={translate("First Name")}
            />
          </Form.Control>
        </Form.Field>
        <Form.Field name="lastName">
          <Form.Message className="text-error-item" match="valueMissing">
            {translate("Please enter your last name")}
          </Form.Message>
          <Form.Control asChild required>
            <Input
              className="mb-[52px] sm:mb-11"
              type="text"
              placeholder={translate("Last Name")}
            />
          </Form.Control>
        </Form.Field>
        <Form.Field name="email">
          <Form.Message className="text-error-item" match="valueMissing">
            {translate("Please enter your email address")}
          </Form.Message>
          <Form.Message className="text-error-item" match="typeMismatch">
            {translate("Please enter a valid email address")}
          </Form.Message>
          <Form.Control asChild required type="email">
            <Input
              ref={emailInputRef}
              className="mb-[52px] sm:mb-11"
              type="email"
              autoComplete="email"
              placeholder={translate("Email")}
            />
          </Form.Control>
        </Form.Field>
        <Form.Field name="secret" className="w-full">
          <div>
            <Form.Message match="valueMissing" className="text-error-item">
              {translate("Please enter a password")}
            </Form.Message>
            <Form.Message
              className="text-error-item"
              match={(value) => {
                return (
                  value.length < 8 ||
                  !includesNumber(value) ||
                  !includesSpecialCharacter(value)
                );
              }}
            >
              {translate("Minimum 8 characters, 1 number, 1 special character")}
            </Form.Message>
          </div>
          <div className="relative flex items-center">
            <Form.Control asChild required>
              <Input
                autoComplete="new-password"
                className="pr-[50px]"
                type={visible ? "text" : "password"}
                placeholder={translate("Password")}
              />
            </Form.Control>
            <div className="absolute right-[20px] flex h-full items-center ">
              {visible ? (
                <Hide
                  className="h-[20px] w-[20px] cursor-pointer text-item-secondary hover:text-item-primary"
                  onClick={togglePassword}
                />
              ) : (
                <Show
                  className="h-[20px] w-[20px] cursor-pointer text-item-secondary hover:text-item-primary"
                  onClick={togglePassword}
                />
              )}
            </div>
          </div>
          <Form.Message
            match="typeMismatch"
            forceMatch={!!error}
            className="block py-4 text-error-item"
          >
            <span>
              {translate(error)}
              {error === ALREADY_EXISTS && (
                <>
                  {" "}
                  <span
                    className="cursor-pointer underline"
                    onClick={() => navigate(signInURL, { replace: true })}
                  >
                    {translate("Click to sign in.")}
                  </span>
                </>
              )}
            </span>
          </Form.Message>
        </Form.Field>
        <Form.Field
          name="marketingOptIn"
          className={cn(
            "mb-[52px] mt-[34px] flex items-center",
            error && "mt-0",
          )}
        >
          <CheckboxInput
            defaultChecked={optInMarketing}
            id="opt-in"
            name="marketingOptIn"
          />
          <label
            htmlFor="opt-in"
            className="p2-medium ml-[10px] text-item-secondary"
          >
            {translate("Send me updates and information to keep me engaged")}
          </label>
        </Form.Field>
        <div className="fixed bottom-0 left-0 right-0 flex flex-col items-center border-t border-border bg-surface-primary px-5 py-4 sm:relative sm:border-none sm:p-0">
          <Form.Submit asChild>
            <Button
              size="l"
              className="text-md h-fit w-full max-w-[440px] px-[14px] py-[10px] sm:mt-[8px] sm:text-sm"
              disabled={loading}
              loading={loading}
            >
              {translate("Continue")}
            </Button>
          </Form.Submit>
          {redirect && (
            <div className="flex justify-center pt-4 sm:pt-6">
              <h5 className="p2-medium sm:p1 sm:mb-12">
                {translate("Already have an account?")}{" "}
                <span
                  className="cursor-pointer text-highlightPrimary"
                  onClick={() => navigate(signInURL, { replace: true })}
                >
                  {translate("Sign in here")}
                </span>
              </h5>
            </div>
          )}
        </div>
      </Form.Root>
      <ColumnModal
        hideTrigger
        cols={3}
        open={!!submissionValues}
        onOpenChange={() => setSubmissionValues(undefined)}
        onConfirm={() => {
          // This is always set
          if (submissionValues) {
            signUp(submissionValues);
          }
          setSubmissionValues(undefined); // close modal
        }}
        title={translate("Check Your Email Is Correct")}
        contentClassName="px-6"
        primaryText={translate("Confirm")}
        secondaryText={translate("Edit Email")}
        onCancel={() => {
          setSubmissionValues(undefined);
          // Needs a small delay for the modal close to have gone through
          setTimeout(() => {
            emailInputRef.current?.focus();
          }, 100);
        }}
      >
        <p className="p1-regular">
          {translate(
            "Please check the below email is correct to ensure that you can receive any account related emails.",
          )}
        </p>
        <div className="pb-6 pt-2">
          <span className="p1-bold">{submissionValues?.email}</span>
        </div>
      </ColumnModal>
    </UserScreenWrapper>
  );
};
